// Currency utility functions for Indian Rupees formatting

// Format number as Indian Rupees with proper formatting
export const formatINR = (amount: number): string => {
  if (isNaN(amount)) return '₹0';
  
  // Use built-in Indian locale formatting
  try {
    const formatted = new Intl.NumberFormat('en-IN', {
      style: 'decimal',
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    }).format(Math.round(amount));
    
    return '₹' + formatted;
  } catch (e) {
    // Fallback to manual formatting
    const amountStr = Math.round(amount).toString();
    const len = amountStr.length;
    
    if (len <= 3) {
      return '₹' + amountStr;
    }
    
    // Get last 3 digits
    let result = amountStr.slice(-3);
    let remaining = amountStr.slice(0, -3);
    
    // Add commas for every 2 digits from right to left
    while (remaining.length > 0) {
      if (remaining.length > 2) {
        result = remaining.slice(-2) + ',' + result;
        remaining = remaining.slice(0, -2);
      } else {
        result = remaining + ',' + result;
        remaining = '';
      }
    }
    
    return '₹' + result;
  }
};

// Format price for display
export const formatPrice = (price: number): string => {
  return formatINR(price);
};

// Parse price string to number (remove currency symbols and commas)
export const parsePrice = (priceStr: string): number => {
  const cleaned = priceStr.replace(/[₹,]/g, '');
  return parseFloat(cleaned) || 0;
};

// Compare two prices
export const comparePrices = (price1: number, price2: number): number => {
  return price1 - price2;
};

// Calculate discount percentage
export const calculateDiscount = (originalPrice: number, salePrice: number): number => {
  if (originalPrice <= 0) return 0;
  return Math.round(((originalPrice - salePrice) / originalPrice) * 100);
};

// Calculate sale price from discount percentage
export const calculateSalePrice = (originalPrice: number, discountPercent: number): number => {
  return Math.round(originalPrice * (1 - discountPercent / 100));
};

// Format price range
export const formatPriceRange = (minPrice: number, maxPrice: number): string => {
  if (minPrice === maxPrice) {
    return formatINR(minPrice);
  }
  return `${formatINR(minPrice)} - ${formatINR(maxPrice)}`;
};

// Default currency configuration
export const CURRENCY_CONFIG = {
  code: 'INR',
  symbol: '₹',
  name: 'Indian Rupee',
  decimalDigits: 0, // Indian rupees typically don't show decimals for retail
  locale: 'en-IN'
};

// Export as default for easy importing
const currencyUtils = {
  formatINR,
  formatPrice,
  parsePrice,
  comparePrices,
  calculateDiscount,
  calculateSalePrice,
  formatPriceRange,
  CURRENCY_CONFIG
};

export default currencyUtils;
